<script setup>
import { inject, ref, watch } from 'vue';
import { apiAjouterImport } from '@/services/ServiceModele.js';
import { normerNom, TypeNommage, 
    ENCODAGES_FICHIER, DELIMITEURS_FICHIER, SEPARATEURS_DECIMAUX } from '@/scripts/global.js';
import { 
    getErreurNomNorme,
    getErreurTexteRequis, 
    getErreurValeurListeRequise } from '@/scripts/controlesSaisie.js';
import Dialog from 'primevue/dialog';
import Formulaire from '@/components/generiques/Formulaire.vue';
import SaisieTexte from '@/components/generiques/SaisieTexte.vue';
import SaisieFichier from '@/components/generiques/SaisieFichier.vue';
import SaisieListe from '@/components/generiques/SaisieListe.vue';

const visible = defineModel('visible', {
    type: Boolean
});

const appelerApiModifModele = inject('appelerApiModifModele');
const schema = inject('schema');
const selectionnerImport = inject('selectionnerImport');
const dernierFichierSelectionne = inject('dernierFichierSelectionne');
const ouvrirFenetreFormule = inject('ouvrirFenetreFormule');
const afficherSucces = inject('afficherSucces');

const nouvelImport = ref(getImportInitial());

watch(() => visible.value, (visible) => {
    if (visible) {
        nouvelImport.value = getImportInitial();
        if (nouvelImport.value.fichier) {
            lireLigneEntete();
        }
    }
})

watch(() => nouvelImport.value.libelle, (nouveauLibelle, ancienLibelle) => {
    if (!nouvelImport.value.nom || nouvelImport.value.nom === normerNom(ancienLibelle, TypeNommage.VARIABLE)) {
        nouvelImport.value.nom = normerNom(nouveauLibelle, TypeNommage.VARIABLE);
    }
});

watch(() => nouvelImport.value.fichier, (fichier) => {
    if (fichier != null) {
        lireLigneEntete();
    }
});

function getImportInitial() {
    return {
        nom: 'import_',
        libelle: 'Import ',
        fichier: dernierFichierSelectionne.value,
        encodage: ENCODAGES_FICHIER[0],
        delimiteur: null,
        separateurDecimal: SEPARATEURS_DECIMAUX[0],
        formatDates: 'dd/MM/yyyy',
        booleenSiVrai: 'true',
        booleenSiFaux: 'false',
        delimiteurListe: DELIMITEURS_FICHIER[3]
    };
}

function lireLigneEntete() {
    const fileReader = new FileReader();
    fileReader.onload = function() {
        const contenuFichier = fileReader.result;
        const indiceRetourLigne = contenuFichier.indexOf('\n');
        var ligneEntete;
        if (indiceRetourLigne != -1) {
            ligneEntete = contenuFichier.substring(0, indiceRetourLigne);
        } else {
            ligneEntete = contenuFichier;
        }
        devinerdelimiteur(ligneEntete);
        
        const indiceRetourLigneSuivant = contenuFichier.substring(indiceRetourLigne + 1).indexOf('\n');
        const indiceFinPremiereLigneValeurs = (indiceRetourLigneSuivant != -1 ? indiceRetourLigne + 1 + indiceRetourLigneSuivant : contenuFichier.length);
        if (indiceFinPremiereLigneValeurs > indiceRetourLigne + 1) {
            const premiereLigneValeurs = contenuFichier.substring(indiceRetourLigne + 1, indiceFinPremiereLigneValeurs);
            devinerSeparateurDecimal(premiereLigneValeurs);
        }
    };
    fileReader.readAsText(nouvelImport.value.fichier);
}

function devinerdelimiteur(ligneEntete) {
    let delimPertinent = null;
    let nbElementsMax = 0;
    for (const delim of DELIMITEURS_FICHIER) {
        let nbElements = ligneEntete.split(delim.chaine).length;
        if (nbElements > nbElementsMax) {
            delimPertinent = delim;
            nbElementsMax = nbElements;
        }
    }
    nouvelImport.value.delimiteur = delimPertinent;
}

function devinerSeparateurDecimal(ligneValeurs) {
    const valeurs = ligneValeurs.split(nouvelImport.value.delimiteur?.chaine);
    let separateurPertinent = null;
    let nbElementsMax = 0;
    for (const separateur of SEPARATEURS_DECIMAUX) {
        let nbElements = valeurs.filter(val => val.includes(separateur.chaine)).length;
        if (nbElements > nbElementsMax) {
            separateurPertinent = separateur;
            nbElementsMax = nbElements;
        }
    }
    if (!separateurPertinent) {
        separateurPertinent = SEPARATEURS_DECIMAUX[0];
    }
    nouvelImport.value.separateurDecimal = separateurPertinent;
}

function validerSaisie(importSaisi) {
    const erreurs = {};
    erreurs.nom = getErreurNomNorme(importSaisi.nom, TypeNommage.VARIABLE);
    erreurs.libelle = getErreurTexteRequis(importSaisi.libelle);
    erreurs.encodage = getErreurValeurListeRequise(importSaisi.encodage);
    erreurs.delimiteur = getErreurValeurListeRequise(importSaisi.delimiteur);
    erreurs.separateurDecimal = getErreurValeurListeRequise(importSaisi.separateurDecimal);
    if (!importSaisi.fichier) {
        erreurs.fichier = 'Un fichier d\'exemple doit être fourni';
    } else {
        erreurs.fichier = null;
    }
    return erreurs;
}

function validerFormulaire() {
    nouvelImport.value.validationFormulaire = true;
}

function ajouterImport() {
    const importSouhaite = {
        nom: nouvelImport.value.nom, 
        libelle: nouvelImport.value.libelle, 
        encodage: nouvelImport.value.encodage?.chaine, 
        delimiteur: nouvelImport.value.delimiteur?.chaine, 
        separateurDecimal: nouvelImport.value.separateurDecimal?.chaine, 
        formatDates: nouvelImport.value.formatDates, 
        formatBooleens: `${nouvelImport.value.booleenSiVrai};${nouvelImport.value.booleenSiFaux}`, 
        delimiteurListe: nouvelImport.value.delimiteurListe?.chaine
    };

    appelerApiModifModele(apiAjouterImport, importSouhaite, nouvelImport.value.fichier, (reponseOK) => {
        if (reponseOK) {
            const importCree = schema.value.imports.find(importDonnees => importDonnees.nom === nouvelImport.value.nom);
            selectionnerImport(importCree);
            dernierFichierSelectionne.value = nouvelImport.value.fichier;
            visible.value = false;
            afficherSucces('Modèle d\'import créé');
            ouvrirFenetreFormule(importCree);
        }
    });
}

</script>

<template>
    <Dialog 
    v-model:visible="visible"
    :modal="true">
        <template #header>
            <label class="titre">Ajout d'un modèle d'import</label>
        </template>
        <Formulaire
            :objet="nouvelImport"
            :fonction-validation-saisie="validerSaisie"
            @validation="ajouterImport">
            <label class="intitule">Libellé :</label>
            <SaisieTexte v-model:valeur="nouvelImport.libelle" :id-erreur="'libelle'" :autofocus="true" />
            <label class="intitule">Nom :</label>
            <SaisieTexte v-model:valeur="nouvelImport.nom" :id-erreur="'nom'" />
            <label class="intitule">Fichier d'exemple :</label>
            <SaisieFichier v-model:valeur="nouvelImport.fichier" :id-erreur="'fichier'" />
            <label class="intitule">Encodage :</label>
            <SaisieListe v-model:valeur="nouvelImport.encodage" :id-erreur="'encodage'" :options="ENCODAGES_FICHIER" :optionLabel="'libelle'" :placeholder="'Indéfini'" />
            <label class="intitule">Délimiteur :</label>
            <SaisieListe v-model:valeur="nouvelImport.delimiteur" :id-erreur="'delimiteur'" :options="DELIMITEURS_FICHIER" :optionLabel="'libelle'" :placeholder="'Indéfini'" />
            <label class="intitule">Séparateur décimal :</label>
            <SaisieListe v-model:valeur="nouvelImport.separateurDecimal" :id-erreur="'separateurDecimal'" :options="SEPARATEURS_DECIMAUX" :optionLabel="'libelle'" :placeholder="'Indéfini'" />
            <label class="intitule">Format des dates :</label>
            <SaisieTexte v-model:valeur="nouvelImport.formatDates" :id-erreur="'formatDates'" />
            <label class="intitule">Format des booléens :</label>
            <div class="zone-textes-booleens">
                <SaisieTexte v-model:valeur="nouvelImport.booleenSiVrai" :id-erreur="'booleenSiVrai'" :infobulle="'Texte si vrai'" />
                /
                <SaisieTexte v-model:valeur="nouvelImport.booleenSiFaux" :id-erreur="'booleenSiFaux'" :infobulle="'Texte si faux'" />
            </div>
        </Formulaire>
        <template #footer>
            <button 
                class="bouton btn-primaire fas fa-check" 
                @click="validerFormulaire()"/>
        </template>
    </Dialog>
</template>

<style scoped>

.titre {
    margin-right: 10px;
}

.zone-textes-booleens {
    display: flex;
}

</style>